import { Box, ScreenContainer, SearchInput, ToggleButtonGroup, } from '@pinwheel/origami';
import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useDispatch } from 'react-redux';
import ModalHeader from '../../../components/ModalHeader';
import { useOpts } from '../../../context';
import useLinkNavigate from '../../../hooks/useLinkNavigate';
import { hubApi } from '../../../state/services/hub-api';
import { EventAction } from '../../../types/events';
import { SearchResponseType } from '../../../types/types';
import { InitialProviders } from './InitialProviders';
import SearchTabResults from './SearchTabResults';
import './SearchMain.scss';
import * as utils from './utils';
import { useAppSelector } from '../../../state';
import { selectSearchText, setSearchText, } from '../../../state/slices/slice-search';
import { useGetLocalizedString } from '../../../hooks/useGetLocalizedString';
import { selectExperiments, selectIsCompanyConnectJob, selectLinkTokenPayload, } from '../../../state/slices/slice-link-params';
import { debounce } from '../../../utils';
import { selectHistory } from '../../../state/history';
import { selectHasShownEligiblePlatformPopup, selectUserSkippedEligibilityLookup, setFirstEligiblePlatform, setHasShownEligiblePlatformPopup, } from '../../../state/slices/slice-supplier-eligibility';
import { showBottomSheet } from '../../../state/slices/slice-bottom-sheet';
import { selectMatchedPlatforms } from '../../../state/slices/slice-link-api';
import { cleanPartnerBasedPlatformNames } from '../../../utils/partner-based-string-cleaning';
import eventQueue from '../../../events/queue';
var useModalHeader = function () {
    var _a;
    var isCompanyConnectJob = useAppSelector(selectIsCompanyConnectJob);
    var linkTokenPlatformType = (_a = useAppSelector(selectLinkTokenPayload)) === null || _a === void 0 ? void 0 : _a.platformType;
    var getLocalizedString = useGetLocalizedString();
    var textToUse = isCompanyConnectJob
        ? 'SELECT_YOUR_PAYROLL_PLATFORM'
        : // TODO: We probably want to handle this for T+A as well
            linkTokenPlatformType === 'tax'
                ? 'WHO_IS_YOUR_TAX_PROVIDER'
                : 'WHO_PAYS_YOU';
    return getLocalizedString(textToUse);
};
function SearchMain() {
    var _a;
    var getLocalizedString = useGetLocalizedString();
    var opts = useOpts();
    var fireClientEvent = opts.fireClientEvent, fireProperEventType = opts.fireProperEventType, setSelectedPlatform = opts.setSelectedPlatform, setSelectedEmployer = opts.setSelectedEmployer, setSearchSessionId = opts.setSearchSessionId;
    var history = useAppSelector(selectHistory);
    var dispatch = useDispatch();
    var searchText = useAppSelector(selectSearchText);
    var _b = useState(), lastFocusedAt = _b[0], setLastFocusedAt = _b[1];
    var searchInputRef = useRef(null);
    var modalTitle = useModalHeader();
    var navigate = useLinkNavigate();
    var focusSearchInput = useCallback(function () {
        fireProperEventType(EventAction.TAP_SEARCH, opts);
        setLastFocusedAt(Date.now());
    }, [setLastFocusedAt, fireProperEventType, opts]);
    var allTabOptions = useMemo(function () { return utils.getSearchTabs(getLocalizedString); }, [getLocalizedString]);
    var _c = hubApi.useGetInitialProvidersQuery({}), initialProviders = _c.data, initialProvidersLoading = _c.isLoading;
    var _d = useState(allTabOptions), tabOptions = _d[0], setTabOptions = _d[1];
    var _e = useState(searchText), searchTerm = _e[0], setSearchTerm = _e[1];
    // eslint-disable-next-line react-hooks/exhaustive-deps
    var debounceSetSearchText = useCallback(debounce(function (value) {
        dispatch(setSearchText({ text: value.slice(0, 32) }));
    }, 500), [dispatch, setSearchText]);
    var userSkippedEligibilityCheck = useAppSelector(selectUserSkippedEligibilityLookup);
    var hasShownEligiblePlatformPopup = useAppSelector(selectHasShownEligiblePlatformPopup);
    var experiments = useAppSelector(selectExperiments);
    var eligibleSupplierPlatforms = useAppSelector(selectMatchedPlatforms);
    useEffect(function () {
        debounceSetSearchText(searchTerm);
    }, [debounceSetSearchText, searchTerm]);
    // Filter down the tab options to only those with one result
    useEffect(function () {
        if (initialProviders) {
            var newTabOptions = utils.filterTabs(allTabOptions, initialProviders);
            // If the initial providers tab grid is empty, immediately show the search
            // results view.
            if (newTabOptions.length === 0) {
                focusSearchInput();
            }
            setTabOptions(newTabOptions);
        }
    }, [initialProviders, allTabOptions, focusSearchInput]);
    useEffect(function () {
        setSelectedPlatform(null);
        setSelectedEmployer(null);
        // TODO: PP-5367
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(function () {
        fireClientEvent('input_required');
    }, [fireClientEvent]);
    var _f = hubApi.useLazySearchQuery(), triggerSearch = _f[0], _g = _f[1], searchResults = _g.data, searchLoading = _g.isFetching;
    useEffect(function () {
        void triggerSearch({
            searchText: searchText,
        });
    }, [triggerSearch, searchText]);
    var handleEmployerClick = useCallback(function (result, viewportResults) {
        setSelectedEmployer(result);
        fireProperEventType(EventAction.EMPLOYER_OR_PLATFORM_SELECTED, opts, {
            searchText: searchText,
            viewportResults: viewportResults,
            employerIdOverride: result.id,
        });
        fireClientEvent('select_employer', {
            selectedEmployerId: result.id,
            selectedEmployerName: result.name,
        });
    }, [
        setSelectedEmployer,
        fireProperEventType,
        opts,
        searchText,
        fireClientEvent,
    ]);
    var handlePlatformClick = useCallback(function (result, viewportResults) {
        setSelectedPlatform(result);
        fireClientEvent('select_platform', {
            selectedPlatformId: result.id,
            selectedPlatformName: result.name,
        });
        fireProperEventType(EventAction.EMPLOYER_OR_PLATFORM_SELECTED, opts, {
            searchText: searchText,
            viewportResults: viewportResults,
            platformIdOverride: result.id,
        });
    }, [
        setSelectedPlatform,
        fireClientEvent,
        fireProperEventType,
        opts,
        searchText,
    ]);
    var onResultClick = useCallback(function (result, viewportResults) {
        if (utils.isResultEmployer(result))
            handleEmployerClick(result, viewportResults);
        else
            handlePlatformClick(result, viewportResults);
        navigate('/link-api/login');
        setSearchSessionId(uuidv4());
    }, [handleEmployerClick, handlePlatformClick, navigate, setSearchSessionId]);
    var onTabItemClick = useCallback(function (selection) {
        // Force a staging platform id to test that this works (AT&T):
        // selection.type = 'platform';
        // selection.id = 'd24056e3-f64c-4e98-96ac-4b9806610cb6';
        if (selection.responseType === SearchResponseType.EMPLOYER) {
            setSelectedEmployer(selection);
            fireClientEvent('select_employer', {
                selectedEmployerId: selection.id,
                selectedEmployerName: selection.name,
            });
            fireProperEventType(EventAction.EMPLOYER_OR_PLATFORM_SELECTED, opts, {
                employerIdOverride: selection.id,
            });
        }
        else {
            setSelectedPlatform(selection);
            fireClientEvent('select_platform', {
                selectedPlatformId: selection.id,
                selectedPlatformName: selection.name,
            });
            fireProperEventType(EventAction.EMPLOYER_OR_PLATFORM_SELECTED, opts, {
                platformIdOverride: selection.id,
            });
        }
        navigate('/link-api/login');
    }, [
        navigate,
        setSelectedEmployer,
        fireClientEvent,
        fireProperEventType,
        opts,
        setSelectedPlatform,
    ]);
    var showSearchResults = !!searchText || tabOptions.length === 0;
    var onBack = useMemo(function () {
        var _a, _b, _c;
        // Don't show the back button if `tabOptions` is empty, since we skipped
        // showing the initial providers tab grid when that's the case.
        if (showSearchResults && tabOptions.length) {
            return function () {
                // "Back" moves us to the default suggested tabbed icons view,
                // which we only render when the search text is empty.
                dispatch(setSearchText({ text: '' }));
                setSearchTerm('');
            };
        }
        // Skip past multi-supplier eligibility results if that was the previous screen AND
        // if there were no matching results, otherwise <NoResults> will automatically navigate
        // the user back to this screen.
        if (history.length > 3 &&
            ((_a = history[1]) === null || _a === void 0 ? void 0 : _a.pathname) === '/multi-supplier-eligibility/results' && // previous screen
            eligibleSupplierPlatforms.length === 0) {
            return function () {
                navigate(-1);
                navigate(-1);
                navigate(-1);
            };
        }
        // Need to skip past multi-supplier eligibility lookup if that was the previous screen.
        if (history.length > 2 &&
            ((_b = history[1]) === null || _b === void 0 ? void 0 : _b.pathname) === '/multi-supplier-eligibility/lookup' // previous screen
        ) {
            return function () {
                navigate(-1);
                navigate(-1);
            };
        }
        // We only want an end user to fill out the wizard form once, so after entering search from the wizard,
        // remove the screens from the history so that the back button will not show the wizard anymore.
        if (history.length > 2 &&
            ((_c = history[1]) === null || _c === void 0 ? void 0 : _c.pathname.startsWith('/wizard')) // previous screen
        ) {
            var wizardPagesVisited_1 = history.filter(function (v) {
                return v.pathname.startsWith('/wizard');
            }).length;
            return function () {
                for (var i = 0; i <= wizardPagesVisited_1; i++) {
                    // TODO: change navigate to allow for going back multiple pages (this is
                    // used in a few places).
                    navigate(-1);
                }
            };
        }
        // Else, use default onBack logic
        return null;
    }, [
        showSearchResults,
        tabOptions.length,
        history,
        eligibleSupplierPlatforms.length,
        dispatch,
        navigate,
    ]);
    // If the only 2 screens the user has seen so far are the current (search) screen and eligibility lookup,
    // we want to hide the back button. This would happen if the user skipped the intro screen.
    var hideBackButton = useMemo(function () {
        var _a;
        if (history.length === 2 && // history includes the current screen
            ((_a = history[1]) === null || _a === void 0 ? void 0 : _a.pathname) === '/multi-supplier-eligibility/lookup' // previous screen
        ) {
            return true;
        }
        return false;
    }, [history]);
    useEffect(function () {
        if (userSkippedEligibilityCheck &&
            !hasShownEligiblePlatformPopup &&
            experiments.alterEligibilityCheckSkip &&
            eligibleSupplierPlatforms.length > 0) {
            var eligibleSupplierPlatform = eligibleSupplierPlatforms[0].platform;
            var platformName = cleanPartnerBasedPlatformNames(eligibleSupplierPlatform.name);
            dispatch(setFirstEligiblePlatform(eligibleSupplierPlatform));
            var eventId = 'matchedPlatformFound';
            eventQueue.emit('bottom_sheet', { eventId: eventId, isOpen: true });
            dispatch(showBottomSheet({
                title: getLocalizedString('PAYROLL_ACCOUNT_FOUND'),
                body: getLocalizedString('WE_FOUND_AN_ACCOUNT', [platformName]),
                dismissButtonText: getLocalizedString('CONTINUE_SEARCHING'),
                dismissButtonVariant: 'ghost',
                navigateButtonText: getLocalizedString('MS_ELIGIBILITY_SINGLE_ACCOUNT_CONTINUE', [platformName]),
                navigateButtonVariant: 'solid',
                navigateButtonAction: 'loginWithMatchedPlatform',
                imageId: 'BigStatusIcon-check',
                eventId: eventId,
            }));
            dispatch(setHasShownEligiblePlatformPopup(true));
        }
    }, [
        userSkippedEligibilityCheck,
        hasShownEligiblePlatformPopup,
        getLocalizedString,
        experiments,
        eligibleSupplierPlatforms,
        navigate,
        dispatch,
    ]);
    var _h = useState(tabOptions[0]), tabSelection = _h[0], setTabSelection = _h[1];
    useEffect(function () {
        setTabSelection(tabOptions[0]);
    }, [tabOptions]);
    return (React.createElement(ScreenContainer, { className: "search", "data-testid": "search" },
        React.createElement(Box, { sx: {
                backgroundColor: {
                    type: 'theme',
                    colorLegacy: 'Neutral/0',
                    colorNext: 'background/default',
                },
                boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.1)',
                zIndex: 10,
                // This is because Origami Buttons have a bottom margin if they aren't
                // the last child of a parent (which is dumb), so if there are multiple
                // tabs, we need to set a bottom padding to the parent to match the
                // bottom margin for a single tab.
                paddingBottom: tabOptions.length > 1 ? 0 : 2,
            } },
            React.createElement(ModalHeader, { title: modalTitle, onBack: onBack, hideBackButton: hideBackButton }),
            React.createElement(SearchInput, { value: searchTerm, sx: (_a = {
                        marginTop: '0px',
                        paddingTop: '0px',
                        marginBottom: '16px'
                    },
                    _a[' input'] = {
                        // need to set overflow to hidden because on thin screens (~320px)
                        // the placeholder overflows out of the box
                        // this is replicable some times on Chrome and Safari
                        overflow: 'hidden',
                    },
                    _a), ref: searchInputRef, onFocus: focusSearchInput, onChange: setSearchTerm, placeholder: getLocalizedString('SEARCH_ALL'), "aria-label": getLocalizedString('SEARCH_ALL_EMPLOYERS_PROVIDERS'), lastFocusedAt: lastFocusedAt }),
            !showSearchResults && !initialProvidersLoading && (React.createElement(ToggleButtonGroup, { options: tabOptions, value: tabSelection, role: "tablist", onChange: function (option) {
                    setTabSelection(option);
                    fireProperEventType(EventAction.TAP, opts, {
                        tab: option.value,
                    });
                } }))),
        showSearchResults && (React.createElement(SearchTabResults, { onResultClick: onResultClick, loading: searchLoading, results: searchResults, searchText: searchText })),
        !showSearchResults && (React.createElement(InitialProviders, { tab: tabSelection.value, initialProviders: initialProviders, initialProvidersLoading: initialProvidersLoading, onCantFind: focusSearchInput, onTabItemClick: onTabItemClick }))));
}
export default SearchMain;
